<template lang="pug">
.media-viewer(ref="viewer"
  @pointerdown="onPointerDown"
  @pointermove="onPointerMove"
  @pointerup="onPointerUp"
  @pointercancel="onPointerUp"
  @mouseleave="onPointerUp"
)
  img(
    v-if="!isVideo"
    :src="mediaUrl"
    ref="img"
    :style="imgStyle"
    @load="onImageLoad"
    draggable="false"
  )
  video(
    v-else
    :src="mediaUrl"
    controls
    autoplay
    loop
    muted
    :style="imgStyle"
    ref="img"
  )
  // temporäre Erstellungsbox
  div.face-temp(v-if="tempBox"
    :style="tempBoxStyle")
  FaceBox(
    v-for="face in faces"
    :key="face.index"
    :face="face"
    :scale="scale"
    :selected="selectedFace?.index === face.index"
    :adminMode="adminMode"
    :groups="groups"
    :editingFaceIndex="editingFaceIndex"
    @select="onFaceClicked"
    @submit="$emit('edit-face', $event)"
  )
</template>

<script>
import FaceBox from './FaceBox.vue';

export default {
  components: { FaceBox },
  props: {
    mediaUrl: String,
    isVideo: Boolean,
    faces: { type: Array, default: () => [] },
    selectedFace: Object,
    adminMode: Boolean,
    scale: { type: Number, default: 1 },
    translateX: { type: Number, default: 0 },
    translateY: { type: Number, default: 0 },
    groups: Array,
    editingFaceIndex: Number
  },
  data() {
    return {
      // Panning
      panning: false,
      panStartX: 0,
      panStartY: 0,

      // Erstellen neuer Boxen
      creating: false,
      createStart: null, // {x,y} in Bild-Koords
      tempBox: null // {x,y,w,h} in Bild-Koords
    };
  },
  computed: {
    imgStyle() {
      return {
        transform: `scale(${this.scale}) translate(${this.translateX}px, ${this.translateY}px)`,
        transformOrigin: 'top left',
        userSelect: 'none'
      };
    },
    tempBoxStyle() {
      if (!this.tempBox) return {};
      const b = this.tempBox;
      return {
        position: 'absolute',
        left: (b.x * this.scale) + 'px',
        top: (b.y * this.scale) + 'px',
        width: (b.w * this.scale) + 'px',
        height: (b.h * this.scale) + 'px',
        border: '2px dashed rgba(0,150,255,0.8)',
        background: 'rgba(0,150,255,0.08)',
        pointerEvents: 'none',
        transformOrigin: 'top left'
      };
    }
  },
  methods: {
    onImageLoad(e) {
      const el = e.target;
      this.$emit('image-loaded', { width: el.naturalWidth, height: el.naturalHeight });
    },

    // Hilfsfunktion: Client-Koordinate -> Bild-Koordinate (un-skaliert, ohne translate)
    clientToImageCoords(clientX, clientY) {
      const viewer = this.$refs.viewer;
      const img = this.$refs.img;
      if (!viewer || !img) return { x: 0, y: 0 };
      const rect = viewer.getBoundingClientRect();
      // Position relativ zum Viewer (oben/links = 0)
      const vx = clientX - rect.left;
      const vy = clientY - rect.top;
      // Unscale & untranslate -> Bildpixel-Koordinaten
      const imgX = (vx / this.scale) - (this.translateX);
      const imgY = (vy / this.scale) - (this.translateY);
      return { x: imgX, y: imgY };
    },

    // Pointer events - zentral gesteuert
    onPointerDown(e) {
      // Wenn Linksklick + Shift (oder Alt/Meta nach Wunsch), starten wir "create new face"
      if (this.adminMode && e.button === 0 && (e.shiftKey || e.ctrlKey)) {
        const p = this.clientToImageCoords(e.clientX, e.clientY);
        this.creating = true;
        this.createStart = p;
        this.tempBox = { x: p.x, y: p.y, w: 0, h: 0 };
        // Capture pointer to continue receiving events even outside element
        e.target.setPointerCapture?.(e.pointerId);
        return;
      }

      // Andernfalls normaler Pan (nur mittlere oder linker Klick ohne modifier)
      if (e.button === 0 || e.button === 1) {
        this.panning = true;
        this.panStartX = e.clientX;
        this.panStartY = e.clientY;
        e.target.setPointerCapture?.(e.pointerId);
      }
    },

    onPointerMove(e) {
      if (this.creating) {
        const p = this.clientToImageCoords(e.clientX, e.clientY);
        const sx = this.createStart.x;
        const sy = this.createStart.y;
        this.tempBox.w = p.x - sx;
        this.tempBox.h = p.y - sy;
        // Do not emit yet, only visual feedback
        return;
      }

      if (this.panning) {
        const dx = e.clientX - this.panStartX;
        const dy = e.clientY - this.panStartY;
        // Emittiere die Veränderung in CSS-Pixel; Upstream rechnet selbst (wie vorher)
        this.$emit('drag', { dx, dy });
        this.panStartX = e.clientX;
        this.panStartY = e.clientY;
      }
    },

    onPointerUp(e) {
      // finalize create
      if (this.creating) {
        this.creating = false;
        e.target.releasePointerCapture?.(e.pointerId);

        const box = this.tempBox;
        this.tempBox = null;

        // minimale Größe prüfen
        if (!box) return;
        const absW = Math.abs(box.w);
        const absH = Math.abs(box.h);
        if (absW < 10 || absH < 10) return;

        // Normalisieren (x,y = top-left)
        let x = box.x;
        let y = box.y;
        let w = box.w;
        let h = box.h;
        if (w < 0) { x = x + w; w = -w; }
        if (h < 0) { y = y + h; h = -h; }

        // Die erzeugte Box als Bildkoordinaten weitergeben
        this.$emit('new-face', { x: Math.round(x), y: Math.round(y), w: Math.round(w), h: Math.round(h) });
        return;
      }

      // finalize pan
      if (this.panning) {
        this.panning = false;
        e.target.releasePointerCapture?.(e.pointerId);
      }
    },

    onFaceClicked(face) {
      this.$emit('face-clicked', face);
    }
  }
};
</script>

<style scoped lang="less">
.media-viewer {
  position: absolute;
  overflow: hidden;
  cursor: grab;
  width: 100%;
  height: 100%;

  img, video {
    display: block;
    max-width: none;
    position: absolute;
    top: 0;
    left: 0;
    user-select: none;
    -webkit-user-drag: none;
  }
}

.face-temp {
  position: absolute;
  z-index: 50;
}
</style>
